import {
    hasNewActivityFailure,
    hasNewActivityWarning,
} from './attention-transition';
import {
    compareActivityJobsNewestFirst,
    isCompletedActivityJobStatus,
    isFailedActivityJobStatus,
} from './job';
import type { ActivityIntent, ActivityItem, ActivityKey } from './models';
import {
    activityIntentFor,
    activitySectionFor,
    hasActivityWarning,
} from './policy';

export type ActivityTransitionSource =
    | 'live'
    | 'registration'
    | 'hydration'
    | 'replay'
    | 'repair';

export type ActivityToastDecision =
    | { readonly eligible: false }
    | {
          readonly eligible: true;
          readonly kind: 'failure' | 'success' | 'warning';
          readonly key: ActivityKey;
          readonly intent: ActivityIntent | null;
          readonly description: string | null;
      };

export type EligibleActivityToastDecision = Extract<
    ActivityToastDecision,
    { readonly eligible: true }
>;

export const NO_ACTIVITY_TOAST: ActivityToastDecision = { eligible: false };

const SILENT_ACTIVITY_TOAST_JOB_TYPES = new Set([
    'DocumentClassification',
    'DocumentIntelligence',
]);

export function decideActivityToast(input: {
    readonly source: ActivityTransitionSource;
    readonly previous: ActivityItem | null;
    readonly next: ActivityItem | null;
}): ActivityToastDecision {
    if (
        (input.source !== 'live' && input.source !== 'registration') ||
        !input.next ||
        input.previous === input.next
    ) {
        return NO_ACTIVITY_TOAST;
    }

    if (
        input.next.jobs.every((job) =>
            SILENT_ACTIVITY_TOAST_JOB_TYPES.has(job.jobType)
        )
    ) {
        return NO_ACTIVITY_TOAST;
    }

    const previousSection = input.previous
        ? activitySectionFor(input.previous)
        : null;
    const nextSection = activitySectionFor(input.next);

    if (
        nextSection === 'needs-attention' &&
        hasNewActivityFailure(input.previous, input.next)
    ) {
        return {
            eligible: true,
            kind: 'failure',
            key: input.next.key,
            intent: null,
            description: newTransitionMessage(
                input.previous,
                input.next,
                (job) => isFailedActivityJobStatus(job.status)
            ),
        };
    }

    if (
        nextSection === 'needs-attention' &&
        hasActivityWarning(input.next) &&
        hasNewActivityWarning(input.previous, input.next)
    ) {
        return {
            eligible: true,
            kind: 'warning',
            key: input.next.key,
            intent: activityIntentFor(input.next),
            description: newTransitionMessage(
                input.previous,
                input.next,
                (job) => job.warning,
                (previousJob, job) => previousJob?.warning !== job.warning
            ),
        };
    }

    if (
        nextSection === 'ready' &&
        previousSection !== 'ready' &&
        (input.source === 'registration' ||
            hasNewTerminalStatus(
                input.previous,
                input.next,
                isCompletedActivityJobStatus
            ))
    ) {
        return {
            eligible: true,
            kind: 'success',
            key: input.next.key,
            intent: activityIntentFor(input.next),
            description: newTransitionMessage(
                input.previous,
                input.next,
                (job) => isCompletedActivityJobStatus(job.status)
            ),
        };
    }

    return NO_ACTIVITY_TOAST;
}

function hasNewTerminalStatus(
    previous: ActivityItem | null,
    next: ActivityItem,
    matchesStatus: (status: ActivityItem['jobs'][number]['status']) => boolean,
    matchesJob: (job: ActivityItem['jobs'][number]) => boolean = () => true
): boolean {
    return next.jobs.some((job) => {
        if (!matchesStatus(job.status) || !matchesJob(job)) return false;

        const previousJob = previous?.jobs.find(
            (candidate) => candidate.jobId === job.jobId
        );
        return previousJob?.status !== job.status;
    });
}

function newTransitionMessage(
    previous: ActivityItem | null,
    next: ActivityItem,
    matchesJob: (job: ActivityItem['jobs'][number]) => boolean,
    isNew: (
        previousJob: ActivityItem['jobs'][number] | undefined,
        job: ActivityItem['jobs'][number]
    ) => boolean = (previousJob, job) => previousJob?.status !== job.status
): string | null {
    const transitioned = next.jobs
        .filter((job) => {
            if (!matchesJob(job)) return false;

            const previousJob = previous?.jobs.find(
                (candidate) => candidate.jobId === job.jobId
            );
            return isNew(previousJob, job);
        })
        .sort(compareActivityJobsNewestFirst)[0];

    return transitioned?.message ?? null;
}
